import React, { useState, useEffect } from "react";

// Exact recreation of the Figma design
const SharePopupExact = ({
  referralLink = "",
  referralCount = 0,
  onClose,
  className = "",
  style = {},
  ...props
}) => {
  const [copied, setCopied] = useState(false);
  const [deviceType, setDeviceType] = useState("desktop");
  const [hoveredBadge, setHoveredBadge] = useState(null);

  useEffect(() => {
    const detectDevice = () => {
      const width = window.innerWidth;
      if (width >= 1200) setDeviceType("desktop");
      else if (width >= 768) setDeviceType("tablet");
      else setDeviceType("mobile");
    };

    detectDevice();
    window.addEventListener("resize", detectDevice);
    return () => window.removeEventListener("resize", detectDevice);
  }, []);

  const handleCopy = async () => {
    if (referralLink && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error("Copy failed:", error);
      }
    }
  };

  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";

  // Reward level definitions - based on ARCHITECTURE.md
  const rewardLevels = {
    0: {
      title: "Getting Started",
      description: "Share your link to start earning rewards",
      icon: "🚀",
    },
    3: {
      title: "Beta Access",
      description: "Exclusive beta access to FootyFlowStates",
      icon: "🎯",
    },
    5: {
      title: "Premium Trial",
      description: "Free premium trial at launch",
      icon: "⭐",
    },
    10: {
      title: "Founding Member",
      description: "Founding member status + lifetime discount",
      icon: "🏆",
    },
  };

  // Interactive badge component
  const RewardBadge = ({ number, left, top, isAchieved }) => {
    const isHovered = hoveredBadge === number;
    const reward = rewardLevels[number];

    return (
      <div
        style={{
          position: "absolute",
          left,
          top,
          cursor: "pointer",
        }}
        onMouseEnter={() => setHoveredBadge(number)}
        onMouseLeave={() => setHoveredBadge(null)}
      >
        {/* Badge circle */}
        <div
          style={{
            width: "58px",
            height: "58px",
            backgroundColor: "#FCE89C",
            borderRadius: "40.15px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "44.6px",
            fontFamily: "Inter, sans-serif",
            fontWeight: "700",
            color: "#43B477",
            zIndex: 10,
            boxShadow: isHovered
              ? "0 8px 24px rgba(0, 0, 0, 0.25)"
              : "0 4px 12px rgba(0, 0, 0, 0.15)",
            transition: "all 300ms ease",
            transform: isHovered ? "scale(1.1)" : "scale(1)",
          }}
        >
          {number}
        </div>

        {/* Tooltip */}
        {isHovered && (
          <div
            style={{
              position: "absolute",
              bottom: "70px",
              left: number === 0 ? "0px" : number >= 5 ? "auto" : "50%", // Special positioning for No.0 badge
              right: number >= 5 && number !== 0 ? "0px" : "auto",
              transform:
                number === 0
                  ? "none"
                  : number >= 5
                    ? "none"
                    : "translateX(-50%)",
              backgroundColor: "rgba(13, 14, 32, 0.95)",
              color: "white",
              padding: "12px 16px",
              borderRadius: "12px",
              fontSize: "14px",
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              lineHeight: "1.4",
              minWidth: "200px",
              maxWidth: "250px",
              textAlign: "center",
              zIndex: 20,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              animation: "fadeIn 200ms ease-out",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: "16px" }}>{reward.icon}</span>
              <span style={{ fontWeight: "600" }}>{reward.title}</span>
            </div>
            <div style={{ fontSize: "12px", opacity: 0.9 }}>
              {reward.description}
            </div>

            {/* Tooltip arrow */}
            <div
              style={{
                position: "absolute",
                bottom: "-6px",
                left: number === 0 ? "29px" : number >= 5 ? "80%" : "50%", // Adjust arrow for No.0 badge
                transform: number === 0 ? "none" : "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid rgba(13, 14, 32, 0.95)",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  // Exact positions and styles from Figma data
  const containerWidth = isMobile ? "100%" : isTablet ? "500px" : "700px";
  const containerHeight = isMobile ? "auto" : "619px";

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "2rem",
      }}
      className={className}
      onClick={onClose}
      {...props}
    >
      <div
        style={{
          width: containerWidth,
          height: containerHeight,
          maxWidth: "100%",
          maxHeight: "90vh",
          position: "relative",
          margin: "0 auto", // Ensure centering
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Interactive Trophy - positioned OUTSIDE popup container to avoid clipping */}
        {!isMobile && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "-54px", // Overhang the popup more
              width: "120px",
              height: "135px",
              zIndex: 25,
              animation: `trophyBounce 2s infinite ease-in-out`,
              cursor: "pointer",
            }}
            onMouseEnter={() => setHoveredBadge("trophy")}
            onMouseLeave={() => setHoveredBadge(null)}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundImage: `url('/popup/trophy.png')`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                transition: "all 300ms ease",
                transform:
                  hoveredBadge === "trophy" ? "scale(1.1)" : "scale(1)",
                filter:
                  hoveredBadge === "trophy"
                    ? "drop-shadow(0 8px 16px rgba(0,0,0,0.3))"
                    : "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
              }}
            />

            {/* Trophy tooltip */}
            {hoveredBadge === "trophy" && (
              <div
                style={{
                  position: "absolute",
                  top: "145px", // Position below the trophy
                  right: "0px",
                  backgroundColor: "rgba(13, 14, 32, 0.95)",
                  color: "white",
                  padding: "16px 20px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  lineHeight: "1.4",
                  minWidth: "220px",
                  maxWidth: "280px",
                  textAlign: "center",
                  zIndex: 30,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  animation: "fadeIn 200ms ease-out",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "6px",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>🏆</span>
                  <span style={{ fontWeight: "700", fontSize: "16px" }}>
                    Founding Member
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.9,
                    marginBottom: "8px",
                  }}
                >
                  {rewardLevels[10].description}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.7,
                    fontStyle: "italic",
                    color: "#FCE89C",
                  }}
                >
                  {referralCount >= 10
                    ? "✅ Unlocked!"
                    : `${10 - referralCount} more referrals to unlock`}
                </div>

                {/* Tooltip arrow */}
                <div
                  style={{
                    position: "absolute",
                    top: "-6px", // Arrow now points up
                    right: "40px",
                    width: 0,
                    height: 0,
                    borderLeft: "6px solid transparent",
                    borderRight: "6px solid transparent",
                    borderBottom: "6px solid rgba(13, 14, 32, 0.95)", // Arrow pointing up
                  }}
                />
              </div>
            )}

            {/* CSS keyframes for trophy bounce animation */}
            <style>
              {`
                @keyframes trophyBounce {
                  0%, 20%, 50%, 80%, 100% {
                    transform: translateY(0);
                  }
                  40% {
                    transform: translateY(-8px);
                  }
                  60% {
                    transform: translateY(-4px);
                  }
                }
              `}
            </style>
          </div>
        )}

        {/* No.0 badge - positioned OUTSIDE popup container to avoid clipping */}
        {!isMobile && (
          <div
            style={{
              position: "absolute",
              left: "-40px",
              top: "369px",
              zIndex: 25,
            }}
          >
            <RewardBadge
              number={0}
              left="0"
              top="0"
              isAchieved={referralCount >= 0}
            />
          </div>
        )}

        {/* Main popup frame - matches Figma "Referral Rewards Popup" */}
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            // Figma background with the football field image
            backgroundImage: `url('/popup/popup-bg.png')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Referral count above text - repositioned */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: "208px", // Positioned 8px above text start (36px + 84px input + 16px margin + 72px text + 8px gap)
              width: "200px",
              height: "200px",
              display: isMobile ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "180px", // Larger size for better visibility
              fontFamily: "Inter, sans-serif", // Exact from Figma
              fontWeight: "400", // Regular from Figma
              // Figma color: r:0.231, g:0.432, b:0.755, a:1 = #3B6EC1
              color: "#3B6EC1",
              lineHeight: "1",
              textAlign: "center",
              zIndex: 5,
            }}
          >
            {referralCount}
          </div>

          {/* Interactive reward badges - positioned exactly from Figma data (INSIDE popup container) */}
          {!isMobile && (
            <>
              <RewardBadge
                number={3}
                left="178px"
                top="290px"
                isAchieved={referralCount >= 3}
              />
              <RewardBadge
                number={5}
                left="418px"
                top="150px"
                isAchieved={referralCount >= 5}
              />
            </>
          )}

          {/* Mobile version - referral count with progress indicators */}
          {isMobile && (
            <div
              style={{
                position: "absolute",
                top: "40px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  fontSize: "4rem",
                  marginBottom: "10px",
                }}
              >
                ⚽
              </div>
              <div
                style={{
                  fontSize: "3rem",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "400",
                  color: "#3B6EC1",
                  lineHeight: "1",
                  marginBottom: "15px",
                }}
              >
                {referralCount}
              </div>

              {/* Mobile progress indicators */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                {[0, 3, 5, 10].map((level) => (
                  <div
                    key={level}
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "12px",
                      backgroundColor: "#FCE89C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#43B477",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {level}
                  </div>
                ))}
              </div>

              {/* Mobile reward indicator */}
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  fontFamily: "Inter, sans-serif",
                  color: "#FFFFFF",
                  textAlign: "center",
                  textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                  opacity: 0.9,
                }}
              >
                {referralCount >= 10
                  ? "🏆 Founding Member Status!"
                  : referralCount >= 5
                    ? "⭐ Premium Trial - 5 more for Founding Member"
                    : referralCount >= 3
                      ? "🎯 Beta Access - 2 more for Premium Trial"
                      : "🚀 Share to unlock Beta Access"}
              </div>
            </div>
          )}

          {/* Message and Link Container - positioned below the "0" */}
          <div
            style={{
              position: "absolute",
              left: "36px",
              bottom: "36px", // Position from bottom
              right: "36px", // Use right instead of width for better responsiveness
              maxWidth: "628px",
              margin: "0 auto", // Center the container
              zIndex: 15,
            }}
          >
            {/* Main message text - from Figma */}
            <div
              style={{
                marginBottom: "16px", // From Figma itemSpacing
                textAlign: "center",
              }}
            >
              <div
                style={{
                  // Figma text style
                  fontFamily: "Poppins, sans-serif", // Exact from Figma
                  fontSize: isMobile ? "18px" : "24px", // Exact from Figma
                  fontWeight: "600", // SemiBold from Figma
                  color: "#FFFFFF", // White from Figma
                  lineHeight: isMobile ? "26px" : "36px", // 100% line height from Figma
                  textShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
                  textAlign: "center",
                }}
              >
                Winners thrive in teams.
                <br />
                Share your link with your teammates.
              </div>
            </div>

            {/* Copy Link Container - from Figma "CopyLink" frame */}
            <div
              style={{
                width: "100%",
                height: isMobile ? "auto" : "84px", // Exact from Figma
                // Figma background: opacity 0.7, color r:0.851, g:0.851, b:0.851, a:1 = #D9D9D9
                backgroundColor: "rgba(217, 217, 217, 0.7)",
                borderRadius: "22px", // Exact from Figma
                // Figma padding: left: 24, right: 10, top: 10, bottom: 10
                padding: isMobile ? "12px 16px" : "10px",
                display: "flex",
                alignItems: "center",
                // Figma itemSpacing
                gap: "10px",
              }}
            >
              {/* Email Entry frame area */}
              <div
                style={{
                  flex: 1,
                  height: isMobile ? "48px" : "64px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                  position: "relative",
                }}
              >
                <input
                  type="text"
                  readOnly
                  value={referralLink}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    outline: "none",
                    backgroundColor: "transparent",
                    fontSize: isMobile ? "13px" : "14px",
                    fontFamily: "ui-monospace, monospace",
                    color: "#374151",
                  }}
                />
              </div>

              {/* Copy button - exact from Figma */}
              <button
                onClick={handleCopy}
                style={{
                  height: isMobile ? "48px" : "64px",
                  // Figma padding: left: 28.44px, right: 28.44px, top: 14.22px, bottom: 14.22px
                  padding: isMobile ? "12px 20px" : "14.22px 28.44px",
                  // Exact white background from Figma
                  backgroundColor: copied ? "#10B981" : "#FFFFFF",
                  // Figma text color: r:0.051, g:0.055, b:0.125 = #0D0E20 (dark blue-black)
                  color: copied ? "white" : "#0D0E20",
                  border: "none",
                  borderRadius: "20px", // Exact from Figma cornerRadius
                  fontSize: isMobile ? "16px" : "25.28px", // Exact from Figma fontSize
                  fontWeight: "600", // SemiBold from Figma
                  fontFamily: "Inter, sans-serif", // Exact from Figma fontFamily
                  cursor: "pointer",
                  transition: "all 200ms ease",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px", // Figma itemSpacing
                  minWidth: isMobile ? "auto" : "153.89px", // Exact from Figma width
                }}
                onMouseEnter={(e) => {
                  if (!copied) {
                    e.target.style.backgroundColor = "#F3F4F6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!copied) {
                    e.target.style.backgroundColor = "#FFFFFF";
                  }
                }}
              >
                <span>{copied ? "✓ Copied!" : "Copy"}</span>
                {!copied && (
                  <img
                    src="/icons/copy-icon.png"
                    alt=""
                    style={{
                      width: "24px",
                      height: "24px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Close Button - positioned at top-right of screen */}
        <button
          onClick={onClose}
          style={{
            position: "fixed",
            top: "2rem", // 32px from top of screen
            right: "2rem", // 32px from right of screen
            background: "rgba(255, 255, 255, 0.95)",
            border: "none",
            borderRadius: "50%",
            width: "48px",
            height: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 1001, // Higher than popup overlay
            fontSize: "24px",
            color: "#6B7280",
            transition: "all 200ms ease",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 1)";
            e.target.style.transform = "scale(1.1)";
            e.target.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.25)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 0.95)";
            e.target.style.transform = "scale(1)";
            e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SharePopupExact;
